import React, { FormEvent, useEffect, useState } from 'react';
import Shell from '../../components/Shell';
import api from '../../services/api';

const ADMIN_EMAIL_DOMAIN = 'consularcapital.com';

function isConsularAdminEmail(email: string) {
  return email.toLowerCase().trim().endsWith(`@${ADMIN_EMAIL_DOMAIN}`);
}

type UserRow = {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  entitlements?: string[];
  active: boolean;
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [grantAdmin, setGrantAdmin] = useState(false);
  const [grantRestructuring, setGrantRestructuring] = useState(false);
  const [msg, setMsg] = useState('');

  async function load() {
    const { data } = await api.get('/admin/users');
    setUsers(data);
  }

  useEffect(() => {
    load().catch(() => setMsg('Failed to load users'));
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setMsg('');
    if (grantAdmin && !isConsularAdminEmail(email)) {
      setMsg(`Admin accounts must use an @${ADMIN_EMAIL_DOMAIN} email`);
      return;
    }
    try {
      const roles = grantAdmin ? ['admin', 'investor'] : ['investor'];
      const { data } = await api.post('/admin/users', {
        email,
        password,
        firstName,
        lastName,
        roles,
        entitlements: grantRestructuring ? ['restructuring'] : [],
      });
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setGrantAdmin(false);
      setGrantRestructuring(false);
      if (data?.inviteError) {
        setMsg(
          `User created, but invite email failed: ${data.inviteError}`,
        );
      } else if (data?.inviteSent) {
        setMsg('User created and invite email sent.');
      } else {
        setMsg('User created (no invite email sent).');
      }
      await load();
    } catch (err: any) {
      setMsg(err?.response?.data?.message || 'Create failed');
    }
  }

  async function patchUser(id: string, body: Record<string, unknown>) {
    setMsg('');
    try {
      await api.patch(`/admin/users/${id}`, body);
      await load();
    } catch (err: any) {
      const message = err?.response?.data?.message;
      setMsg(
        Array.isArray(message)
          ? message.join(', ')
          : message || 'Update failed',
      );
    }
  }

  async function toggleActive(u: UserRow) {
    await patchUser(u._id, { active: !u.active });
  }

  async function toggleRestructuring(u: UserRow) {
    const current = u.entitlements || [];
    const has = current.includes('restructuring');
    const entitlements = has
      ? current.filter((e) => e !== 'restructuring')
      : [...current, 'restructuring'];
    await patchUser(u._id, { entitlements });
  }

  async function toggleAdmin(u: UserRow) {
    const hasAdmin = u.roles.includes('admin');
    let roles: string[];
    if (hasAdmin) {
      const withoutAdmin = u.roles.filter((r) => r !== 'admin');
      roles = withoutAdmin.length ? withoutAdmin : ['investor'];
    } else {
      if (!isConsularAdminEmail(u.email)) {
        setMsg(`Admin accounts must use an @${ADMIN_EMAIL_DOMAIN} email`);
        return;
      }
      roles = u.roles.includes('admin') ? u.roles : [...u.roles, 'admin'];
    }
    await patchUser(u._id, { roles });
  }

  const canGrantAdminOnCreate = isConsularAdminEmail(email);

  return (
    <Shell title="Admin · Users">
      <h1 className="font-display text-3xl">Users</h1>
      <form
        onSubmit={onCreate}
        className="mt-6 grid max-w-2xl gap-3 sm:grid-cols-2"
      >
        <input
          required
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <input
          required
          placeholder="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <input
          required
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (
              grantAdmin &&
              e.target.value &&
              !isConsularAdminEmail(e.target.value)
            ) {
              setGrantAdmin(false);
            }
          }}
        />
        <input
          required
          type="password"
          minLength={8}
          placeholder="Temp password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <label className="sm:col-span-2 flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={grantAdmin}
            disabled={!canGrantAdminOnCreate}
            onChange={(e) => setGrantAdmin(e.target.checked)}
          />
          Grant admin permissions
          {!canGrantAdminOnCreate && (
            <span className="text-ink-700/70">
              (requires @{ADMIN_EMAIL_DOMAIN})
            </span>
          )}
        </label>
        <label className="sm:col-span-2 flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={grantRestructuring}
            onChange={(e) => setGrantRestructuring(e.target.checked)}
          />
          Grant Restructuring page access
        </label>
        <button
          type="submit"
          className="sm:col-span-2 bg-navy-900 py-2 text-sm font-semibold text-mist-50 hover:bg-navy-700"
        >
          Create user
        </button>
      </form>
      {msg && <p className="mt-3 text-sm text-ink-700">{msg}</p>}
      <ul className="mt-10 divide-y divide-mist-200 border-y border-mist-200">
        {users.map((u) => {
          const hasRestructuring = (u.entitlements || []).includes(
            'restructuring',
          );
          const isAdminUser = u.roles.includes('admin');
          const canBecomeAdmin = isConsularAdminEmail(u.email);
          return (
            <li
              key={u._id}
              className="flex flex-wrap items-center justify-between gap-3 py-4"
            >
              <div>
                <p className="font-medium">
                  {u.firstName} {u.lastName}
                </p>
                <p className="text-sm text-ink-700">
                  {u.email} · {u.roles.join(', ')} ·{' '}
                  {u.active ? 'active' : 'revoked'}
                  {!isAdminUser && (
                    <>
                      {' '}
                      · restructuring{' '}
                      {hasRestructuring ? 'granted' : 'not granted'}
                    </>
                  )}
                  {isAdminUser && <> · restructuring (admin)</>}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                {(isAdminUser || canBecomeAdmin) && (
                  <button
                    type="button"
                    onClick={() => toggleAdmin(u)}
                    className="text-sm underline"
                  >
                    {isAdminUser ? 'Revoke admin' : 'Grant admin'}
                  </button>
                )}
                {!isAdminUser && (
                  <button
                    type="button"
                    onClick={() => toggleRestructuring(u)}
                    className="text-sm underline"
                  >
                    {hasRestructuring
                      ? 'Revoke restructuring'
                      : 'Grant restructuring'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => toggleActive(u)}
                  className="text-sm underline"
                >
                  {u.active ? 'Revoke' : 'Reactivate'}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </Shell>
  );
}
